import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Trophy, Lock, X } from "lucide-react";
import { TROPHIES, trophies, useTrophyCount } from "@/lib/trophies";
import GlobalTrophyTicker from "@/pages/idevra/GlobalTrophyTicker";
import { sfx } from "@/lib/sound";

const TIER_COLORS = { bronze: "#B45309", silver: "#64748B", gold: "#D97706", platinum: "#0284C7" };
const TIER_LABELS = { bronze: "BRONZE", silver: "SILVER", gold: "GOLD", platinum: "PLATINUM" };

export default function TrophyCabinet({ onClose }) {
  const count = useTrophyCount();
  const pct = Math.round((count / TROPHIES.length) * 100);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        sfx.back();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4 sm:p-8"
      data-testid="trophy-cabinet"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
    >
      <button
        type="button"
        aria-label="Close cabinet"
        data-testid="trophy-cabinet-backdrop"
        onClick={() => {
          sfx.back();
          onClose();
        }}
        className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-md"
      />
      <motion.div
        className="iv-glass relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl"
        initial={{ scale: 0.94, y: 22 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 12, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
      >
        <div className="border-b border-white/60 px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <Trophy size={16} color="#D97706" />
              <span className="f-syncopate text-[11px] tracking-[0.35em] text-[#0F172A]">TROPHY CABINET</span>
            </span>
            <span className="flex items-center gap-3">
              <span className="f-orbitron text-[12px] font-bold text-[#0284C7]" data-testid="trophy-cabinet-count">
                {count} / {TROPHIES.length}
              </span>
              <button
                type="button"
                data-testid="trophy-cabinet-close"
                onClick={() => {
                  sfx.back();
                  onClose();
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
              >
                <X size={13} />
              </button>
            </span>
          </div>
          <div className="iv-stat-track mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#E2E8F0]">
            <motion.div
              className="h-full rounded-full"
              style={{ background: "linear-gradient(90deg,#38BDF8,#0284C7)" }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4 sm:px-6">
          {TROPHIES.map((t, i) => {
            const has = trophies.has(t.id);
            const hidden = t.secret && !has;
            return (
              <motion.div
                key={t.id}
                data-testid={`cabinet-row-${t.id}`}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.3 }}
                className={`flex items-center gap-4 rounded-xl border px-4 py-3 ${
                  has ? "border-white/70 bg-white/50" : "border-transparent bg-[#F1F5F9]/60"
                }`}
              >
                <span
                  className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                  style={
                    has
                      ? {
                          background: `${TIER_COLORS[t.tier]}22`,
                          boxShadow: `0 0 14px ${TIER_COLORS[t.tier]}40`,
                        }
                      : { background: "#E2E8F0" }
                  }
                >
                  <Trophy size={18} color={has ? TIER_COLORS[t.tier] : "#B6C2D1"} />
                  {!has && (
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#94A3B8]">
                      <Lock size={9} color="#fff" />
                    </span>
                  )}
                </span>
                <span className={`min-w-0 flex-1 ${has ? "" : "opacity-55"}`}>
                  <span className="block f-orbitron truncate text-[12px] font-bold text-[#0F172A]">
                    {hidden ? "??? — SECRET TROPHY" : t.name}
                  </span>
                  <span className="block truncate text-[11px] leading-tight text-[#64748B]">
                    {hidden ? "A legendary secret awaits…" : t.desc}
                  </span>
                </span>
                <span
                  className="f-mono shrink-0 rounded-full px-2.5 py-1 text-[8px] tracking-[0.2em]"
                  style={
                    has
                      ? { color: TIER_COLORS[t.tier], background: `${TIER_COLORS[t.tier]}14` }
                      : { color: "#94A3B8", background: "#E2E8F0" }
                  }
                >
                  {has ? TIER_LABELS[t.tier] : "LOCKED"}
                </span>
              </motion.div>
            );
          })}
        </div>

        <div className="border-t border-white/60 px-6 py-3">
          <GlobalTrophyTicker />
        </div>
      </motion.div>
    </motion.div>
  );
}
